import { useState, useEffect } from "react"
import { ShoppingBag, Star, Users, TrendingUp, TrendingDown, Search, Filter, CheckCircle, Award, Zap } from "lucide-react"
import api from "../services/api"
import Badge from "../components/common/Badge"
import { formatPct, formatCurrency } from "../utils/formatters"
import { PageLoader } from "../components/common/LoadingSpinner"
import toast from "react-hot-toast"

const MOCK_PROVIDERS = [
  { id: "m1", name: "Alpha Signals Pro", creator: "quantmaster99", strategy: "EMA Crossover", symbol: "BTC/USDT", timeframe: "4h", win_rate: 71.4, total_return: 142.8, monthly_return: 8.3, subscribers: 1240, price: 49, rating: 4.8, reviews: 234, risk_level: "Medium", verified: true, badges: ["Top Performer", "Consistent"] },
  { id: "m2", name: "RSI Master Signals", creator: "rsitrader", strategy: "RSI Mean Reversion", symbol: "ETH/USDT", timeframe: "1h", win_rate: 68.2, total_return: 89.4, monthly_return: 5.2, subscribers: 876, price: 29, rating: 4.6, reviews: 182, risk_level: "Low", verified: true, badges: ["Steady Growth"] },
  { id: "m3", name: "Breakout Beast", creator: "brkout_king", strategy: "Volatility Breakout", symbol: "BTC/USDT", timeframe: "1d", win_rate: 55.0, total_return: 310.2, monthly_return: 14.1, subscribers: 2100, price: 99, rating: 4.5, reviews: 410, risk_level: "High", verified: true, badges: ["High Returns", "Popular"] },
  { id: "m4", name: "Scalp Ninja", creator: "ninja_scalps", strategy: "Smart Scalping", symbol: "SOL/USDT", timeframe: "15m", win_rate: 73.8, total_return: 67.2, monthly_return: 6.8, subscribers: 540, price: 39, rating: 4.3, reviews: 98, risk_level: "High", verified: false, badges: [] },
  { id: "m5", name: "Grid Maestro", creator: "gridflex", strategy: "Grid Trading", symbol: "ETH/USDT", timeframe: "1h", win_rate: 81.0, total_return: 44.5, monthly_return: 3.5, subscribers: 430, price: 19, rating: 4.7, reviews: 76, risk_level: "Low", verified: true, badges: ["Consistent"] },
  { id: "m6", name: "DCA Champion", creator: "dcaguru", strategy: "DCA Strategy", symbol: "BTC/USDT", timeframe: "1d", win_rate: 90.0, total_return: 38.0, monthly_return: 2.8, subscribers: 3200, price: 0, rating: 4.9, reviews: 890, risk_level: "Low", verified: true, badges: ["Free", "Most Popular"] },
]

const RISK_COLORS = { Low: "green", Medium: "yellow", High: "red" }
const BADGE_COLORS = { "Top Performer": "yellow", "Consistent": "blue", "Steady Growth": "green", "High Returns": "red", "Popular": "purple", "Free": "green", "Most Popular": "purple" }

function ProviderCard({ provider, onSubscribe, subscribed }) {
  const stars = Array.from({ length: 5 }, (_, i) => i < Math.floor(provider.rating))
  return (
    <div className="card hover:border-brand-500/30 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-bold text-white">{provider.name}</h3>
            {provider.verified && <CheckCircle size={14} className="text-brand-400"/>}
          </div>
          <p className="text-xs text-gray-400">by {provider.creator}</p>
          {provider.badges.length > 0 && (
            <div className="flex gap-1.5 flex-wrap mt-1.5">
              {provider.badges.map(b => <Badge key={b} variant={BADGE_COLORS[b] || "gray"}>{b}</Badge>)}
            </div>
          )}
        </div>
        <div className="text-right ml-3">
          {provider.price === 0 ? <p className="text-trading-green font-bold text-lg">Free</p> : <p className="text-white font-bold text-lg">${provider.price}<span className="text-xs text-gray-500 font-normal">/mo</span></p>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[["Win Rate", `${provider.win_rate}%`, "text-trading-green"], ["Monthly", `+${provider.monthly_return}%`, "text-trading-green"], ["Total", `+${provider.total_return}%`, "text-brand-400"]].map(([k,v,c]) => (
          <div key={k} className="bg-surface-100 rounded-xl p-2 text-center">
            <p className="text-[10px] text-gray-500">{k}</p>
            <p className={`font-mono font-bold text-sm ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
        <span className="flex items-center gap-1"><Users size={11}/>{provider.subscribers.toLocaleString()}</span>
        <span className="flex items-center gap-1">{stars.map((s,i) => <Star key={i} size={10} className={s ? "text-trading-yellow fill-trading-yellow" : "text-gray-600"}/>)}{provider.rating}</span>
        <Badge variant={RISK_COLORS[provider.risk_level]}>{provider.risk_level} Risk</Badge>
        <span className="text-gray-500">{provider.symbol} · {provider.timeframe}</span>
      </div>
      <button onClick={() => onSubscribe(provider)} disabled={subscribed} className={`w-full text-sm ${subscribed ? "btn-secondary opacity-60 cursor-default" : "btn-primary"}`}>
        {subscribed ? "✓ Subscribed" : "Subscribe"}
      </button>
    </div>
  )
}

export default function Marketplace() {
  const [providers, setProviders] = useState([])
  const [subs, setSubs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [riskFilter, setRiskFilter] = useState("All")
  const [sort, setSort] = useState("popular")

  useEffect(() => {
    api.get("/marketplace/signals").then(r => setProviders(r.data)).catch(() => setProviders(MOCK_PROVIDERS)).finally(() => setLoading(false))
  }, [])

  async function subscribe(provider) {
    try {
      await api.post(`/marketplace/signals/${provider.id}/subscribe`)
      setSubs(s => [...s, provider.id])
      toast.success(`Subscribed to ${provider.name}`)
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to subscribe to ${provider.name}`)
    }
  }

  const display = (providers.length ? providers : MOCK_PROVIDERS)
    .filter(p => p.name.toLowerCase().includes(search.toLowerCase()) || p.creator.toLowerCase().includes(search.toLowerCase()))
    .filter(p => riskFilter === "All" || p.risk_level === riskFilter)
    .sort((a,b) => sort === "popular" ? b.subscribers - a.subscribers : sort === "returns" ? b.total_return - a.total_return : sort === "winrate" ? b.win_rate - a.win_rate : b.rating - a.rating)

  const totalSubs = (providers.length ? providers : MOCK_PROVIDERS).reduce((s,p) => s + p.subscribers, 0)

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Signal Marketplace</h2>
        <p className="text-sm text-gray-400 mt-1">Subscribe to proven signal providers · {(providers.length || MOCK_PROVIDERS.length)} providers · {totalSubs.toLocaleString()} total subscribers</p>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(() => {
          const src = providers.length ? providers : MOCK_PROVIDERS
          return [
            ["Providers", src.length, "text-white"],
            ["Avg Win Rate", `${(src.reduce((s,p) => s+p.win_rate,0)/src.length).toFixed(0)}%`, "text-trading-green"],
            ["Total Subscribers", src.reduce((s,p) => s+p.subscribers,0).toLocaleString(), "text-brand-400"],
            ["Free Providers", src.filter(p => p.price === 0).length, "text-trading-yellow"],
          ].map(([k,v,c]) => (
            <div key={k} className="card-sm text-center"><p className="text-xs text-gray-400 mb-1">{k}</p><p className={`text-xl font-bold ${c}`}>{v}</p></div>
          ))
        })()}
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input className="input pl-9 w-full text-sm" placeholder="Search providers..." value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <div className="flex gap-2">
          {["All","Low","Medium","High"].map(r => (
            <button key={r} onClick={() => setRiskFilter(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${riskFilter === r ? "bg-brand-500 text-white" : "text-gray-400 hover:text-white bg-surface-100"}`}>{r}</button>
          ))}
        </div>
        <select className="input text-sm" value={sort} onChange={e => setSort(e.target.value)}>
          <option value="popular">Most Popular</option>
          <option value="returns">Best Returns</option>
          <option value="winrate">Highest Win Rate</option>
          <option value="rating">Best Rated</option>
        </select>
      </div>
      {loading ? <PageLoader/> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {display.map(p => <ProviderCard key={p.id} provider={p} onSubscribe={subscribe} subscribed={subs.includes(p.id)}/>)}
          {display.length === 0 && <div className="col-span-3 text-center py-12 text-gray-500"><ShoppingBag size={36} className="mx-auto mb-2"/><p>No providers match your search</p></div>}
        </div>
      )}
    </div>
  )
}
